import { a as BaseGlobal, B as BaseTheme, b as BaseLoader, c as BaseButton, d as BaseCard, e as BaseChat, f as BaseSvgPalette, g as BaseTable, h as BaseTextfield, i as BaseBreakpoints, j as BaseImages, k as BaseSiteNavigation, l as generateIconLookup, _ as _extends } from './base-57e9514e.js';
import React from 'react';
import { ThemeProvider } from 'styled-components';
import { mergeDeepRight } from 'ramda';
import '@amtrav/icons';

const bounceColors = {
  ballBlue: '#1fb9ca',
  blackCoral: '#5d576b',
  blueCrayola: '#0776fc',
  blueMist: '#dcebfc',
  bounceInfo: '#073e97',
  brightYellow: '#fcb727',
  buttonBlue: '#0776fc',
  cornsilk: '#fffaf2',
  cosmicCobalt: '#323988',
  danger: '#e73636',
  denimBlue: '#2531b1',
  diamond: '#c2dfff',
  disabled: '#dddddd',
  dodgerBlue: '#138cff',
  gamboge: '#e79808',
  gunmetal: '#2d3047',
  highlightGold: '#F1BC00',
  independence: '#444f69',
  lavendarMist: '#f3f4fd',
  // used for very light backgrounds
  lightBlueMist: '#f1f7fd',
  lightCyan: '#cffaff',
  lightGray: '#d3dce6',
  // used for borders
  lightGrayBluish: '#d1e0f6',
  mediumSlateBlue: '#886afe',
  midnight: '#15203D',
  navajoWhite: '#ffe7aa',
  palatinateBlue: '#3947de',
  paleLavendar: '#dcd1fe',
  paynesGrey: '#525f7f',
  powderBlue: '#bbe3e0',
  success: '#00cd83',
  tealGreen: '#01897b',
  textColor: '#15203d',
  tuscan: '#F7DAA8',
  vividGamboge: '#ff9c02',
  warning: '#e68e16',
  wildBlueYonder: '#9fa8da',
  greenBeige: '#d8e3ca',
  // new dashboard colors
  rebeccaPurple: '#7236bb',
  lavenderFloral: '#B592DF',
  limon: '#92af68'
};
const bouncenextGreyPalette = {
  50: '#fafafa',
  75: '#f7f7f7',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#d6d6d6',
  400: '#C0C0C0',
  // silver
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121'
};
const bounceCardShadow = 'rgb(0 0 0 / 8%) 0px 1px 8px 0px';
const bounceInputShadow = 'rgb(0 0 0 / 8%) 0px 1px 3px 0px';
const bouncenextGlobalApi = {
  ...BaseGlobal,
  greyPalette: {
    ...bouncenextGreyPalette
  },
  'primary--color': bounceColors.buttonBlue,
  'secondary--color': bounceColors.midnight,
  'textBody--color': bounceColors.textColor,
  'link--color': bounceColors.buttonBlue,
  'textBody--fontFamily': '"Public Sans", Helvetica, Arial, sans-serif',
  'headings--color': bounceColors.textColor,
  // sometimes used in large header fonts
  'dark--color': bounceColors.midnight,
  'light--color': bounceColors.lightBlueMist,
  'accent--color': bounceColors.lightGrayBluish,
  'danger--color': bounceColors.danger,
  'warning--color': bounceColors.warning,
  'success--color': bounceColors.success,
  'info--color': bounceColors.bounceInfo,
  'disabled--color': bounceColors.disabled,
  'border--color': bounceColors.lightGrayBluish,
  'borderRadius--size': '0',
  'icon--contact--bgcolor': '#cdcbcb',
  'icon--air--color': bounceColors.blueCrayola,
  'icon--air--bgcolor': bounceColors.diamond,
  'icon--car--color': bounceColors.gamboge,
  'icon--car--bgcolor': bounceColors.tuscan,
  'icon--country--color': bounceColors.dodgerBlue,
  'icon--country--bgcolor': bounceColors.diamond,
  'icon--fee--color': bounceColors.ballBlue,
  'icon--fee--bgcolor': bounceColors.lightCyan,
  'icon--hotel--color': bounceColors.rebeccaPurple,
  'icon--hotel--bgcolor': bounceColors.lavenderFloral,
  'icon--mile--color': bounceColors.mediumSlateBlue,
  'icon--mile--bgcolor': bounceColors.paleLavendar,
  'icon--night--color': bounceColors.cosmicCobalt,
  'icon--night--bgcolor': bounceColors.wildBlueYonder,
  'icon--policy--color': bounceColors.tealGreen,
  'icon--policy--bgcolor': bounceColors.powderBlue,
  'icon--money--color': bounceColors.vividGamboge,
  'icon--money--bgcolor': bounceColors.navajoWhite,
  'icon--ticket--color': bounceColors.ballBlue,
  'icon--ticket--bgcolor': bounceColors.lightCyan,
  'icon--trip--color': bounceColors.bounceInfo,
  'icon--trip--bgcolor': bounceColors.blueMist,
  'icon--co2--color': bounceColors.limon,
  'icon--co2--bgcolor': bounceColors.greenBeige,
  'icon--default--color': bounceColors.limon,
  'icon--default--bgcolor': bounceColors.limon
};
const BounceNextTheme = {
  ...BaseTheme,
  brand: 'bouncenext',
  logoInterstitial: 'BounceNextInterstitial',
  loader: {
    ...BaseLoader(bouncenextGlobalApi),
    'loadingBar--color': bounceColors.midnight
  },
  global: bouncenextGlobalApi,
  button: {
    ...BaseButton(bouncenextGlobalApi),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(bouncenextGlobalApi),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow,
    'border--color': bouncenextGreyPalette[400]
  },
  chat: {
    ...BaseChat(bouncenextGlobalApi),
    'minimized-chat--color': bounceColors.brightYellow,
    'minimized-icon--color': bounceColors.midnight
  },
  svgPalette: {
    ...BaseSvgPalette(bouncenextGlobalApi),
    'accent--color': bounceColors.blueMist
  },
  table: {
    ...BaseTable(bouncenextGlobalApi)
  },
  textfield: {
    ...BaseTextfield(bouncenextGlobalApi),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(bouncenextGlobalApi),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'BounceNextNavigation',
    'contactBackground--color': bounceColors.midnight,
    'activeLink--color': bounceColors.highlightGold,
    showNavIcons: true
  },
  stylesForConcept: generateIconLookup(bouncenextGlobalApi)
};

const abacusColors = {
  lightGrey: '#f5f5f5',
  lightColor: '#f4f9fd',
  darkBlue: '#2C3F5E',
  blueJeans: '#12a9fb',
  // nav section title
  medBlue: '#048CD6',
  info: '#424243'
};
const globalApi$a = {
  ...BaseGlobal,
  'primary--color': abacusColors.medBlue,
  'link--color': abacusColors.medBlue,
  'light--color': abacusColors.lightColor,
  'info--color': abacusColors.info,
  'pageHeaderText--color': abacusColors.darkBlue,
  'headings--color': abacusColors.darkBlue,
  'textBody--color': abacusColors.darkBlue,
  'borderRadius--size': '0'
};
const AbacusTheme = {
  ...BaseTheme,
  brand: 'abacus',
  brandName: 'AmTrav',
  logoInterstitial: 'AbacusInterstitial',
  loader: {
    ...BaseLoader(globalApi$a)
  },
  global: globalApi$a,
  button: {
    ...BaseButton(globalApi$a),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$a),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$a),
    'minimized-chat--color': abacusColors.medBlue
  },
  table: {
    ...BaseTable(globalApi$a)
  },
  textfield: {
    ...BaseTextfield(globalApi$a),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$a),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'AbacusNavigation',
    'navBorder--color': 'rgba(139, 139, 139, 0.25)',
    showPoweredBy: true,
    'activeLink--color': abacusColors.blueJeans
  }
};

const amtravColors = {
  lightBlue: '#9bd1e4',
  lightBlueMist: '#eff9ff',
  // card
  cyan: '#21a9cd',
  blue: '#156da8',
  darkBlue: '#165d85',
  backgroundCream: '#fffeed',
  // textfield
  danger: '#a12929',
  warning: '#e68e16',
  success: '#4da129',
  liberty: '#6353a3',
  amazonite: '#12c5b2',
  cgBlue: '#007da5',
  cherry: '#db3260',
  mantis: '#75cb44',
  stilDeGrainYellow: '#F7D45E'
};
const globalApi$9 = {
  ...BaseGlobal,
  'primary--color': amtravColors.cyan,
  'secondary--color': amtravColors.blue,
  'link--color': amtravColors.cyan,
  'link--hover--color': amtravColors.cyan,
  'border--color': '#ccc',
  'border--focus--color': amtravColors.lightBlue,
  'danger--color': amtravColors.danger,
  'warning--color': amtravColors.warning,
  'success--color': amtravColors.success,
  'info--color': amtravColors.blue,
  'dark--color': amtravColors.darkBlue,
  'light--color': amtravColors.lightBlueMist,
  'headings--fontFamily': '"Source Sans Pro", system-ui, Arial, sans-serif',
  'textBody--fontFamily': '"Source Sans Pro", system-ui, Arial, sans-serif',
  'pageHeaderText--color': amtravColors.darkBlue,
  'headings--color': amtravColors.darkBlue,
  'pageHeaderBackground--color': 'white'
};
const AdvisorConsoleTheme = {
  ...BaseTheme,
  brand: 'advisorconsole',
  brandName: 'AmTrav',
  loader: {
    ...BaseLoader(globalApi$9)
  },
  global: globalApi$9,
  button: {
    ...BaseButton(globalApi$9)
  },
  card: {
    ...BaseCard(globalApi$9),
    'subheaderBackground--color': amtravColors.lightBlueMist
  },
  chat: {
    ...BaseChat(globalApi$9)
  },
  table: {
    ...BaseTable(globalApi$9),
    'primaryBackground--color': '#eee'
  },
  textfield: {
    ...BaseTextfield(globalApi$9),
    'background--color': '#fff',
    'background--focus--color': amtravColors.backgroundCream,
    'prominentBackground--color': '#fff',
    'prominentBackground--focus--color': amtravColors.backgroundCream,
    enableInnerShadow: true
  },
  breakpoints: BaseBreakpoints(globalApi$9),
  images: {
    ...BaseImages
  }
};

const breakpoint = {
  small: 268,
  medium: 556,
  large: 748,
  xlarge: 1068
};
const ATOMTheme = {
  ...BounceNextTheme,
  brand: 'atom',
  brandName: 'ATOM',
  global: {
    ...bouncenextGlobalApi,
    'textBody--color': '#212121',
    'textBody--fontFamily': '"Roboto", sans-serif',
    'headings--color': '#212121',
    'borderRadius--size': '6px',
    'breakpointSM--size': breakpoint.small,
    'breakpointMD--size': breakpoint.medium,
    'breakpointLG--size': breakpoint.large,
    'breakpointXL--size': breakpoint.xlarge
  },
  button: {
    ...BaseButton(bouncenextGlobalApi),
    'text--fontFamily': '"Roboto", sans-serif',
    'borderRadius--size': '4px',
    'borderWidth--standard': '1px',
    borderTransparent: true
  },
  textfield: {
    ...BaseTextfield(bouncenextGlobalApi),
    'text--color': '#212121',
    'background--color': 'white',
    'borderWidth--standard': '1px',
    'borderRadius--size': '4px',
    'box-shadow': 'none',
    'border--color': bouncenextGreyPalette[400],
    // minHeight ends up being a string when used in TextInput & DropdownSelect
    'minHeight--size': 26,
    'dropdownMinHeight--size': 26
  }
};

const A2bTheme = {
  ...BounceNextTheme,
  brand: 'a2b',
  brandName: 'AmTrav'
};

const CAVA_COLORS = {
  // neutral
  neutral0: '#FFFFFF',
  neutral100: '#FAFAF5',
  neutral200: '#F5F5EB',
  // off white
  neutral500: '#D2D2C8',
  neutral600: '#919183',
  neutral700: '#838377',
  neutral800: '#6E6E64',
  neutral900: '#44443C',
  neutral950: '#14140F',
  // off black

  // Red
  red100: '#FDE7ED',
  red600: '#D20F46',
  // Orange
  orange500: '#FF8C5F',
  // amber
  amber100: '#FFEDB8',
  amber400: '#FFBE00',
  amber700: '#805F00',
  amber900: '#5C4400',
  // lime´
  lime400: '#BEFF50',
  // perk

  // green
  green200: '#D6FFEF',
  green800: '#007D4B',
  // emerald
  emerald50: '#EDFFFD',
  emerald800: '#06806E',
  // cyan
  cyan400: '#82DCFA',
  // blue
  blue50: '#F0F5FF',
  blue900: '#002971',
  // purple
  purple50: '#F1F0FF',
  purple300: '#C8A0FF',
  purple700: '#5647B2',
  // pink
  pink300: '#FAB9FA'
};
const globalApi$8 = {
  ...BaseGlobal,
  'primary--color': CAVA_COLORS.neutral800,
  'secondary--color': CAVA_COLORS.neutral950,
  'light--color': CAVA_COLORS.neutral100,
  'dark--color': CAVA_COLORS.neutral950,
  'border--color': CAVA_COLORS.neutral500,
  'danger--color': CAVA_COLORS.red600,
  'success--color': CAVA_COLORS.green800,
  'info--color': CAVA_COLORS.neutral800,
  'headings--color': CAVA_COLORS.neutral950,
  'link--color': CAVA_COLORS.neutral950,
  'textBody--fontFamily': '"PerkSans", Arial, sans-serif',
  'icon--air--color': '#815ADC',
  'icon--car--color': '#FFCC08',
  'icon--hotel--color': '#F55912',
  'icon--night--color': CAVA_COLORS.neutral700,
  'icon--money--color': CAVA_COLORS.neutral700,
  'icon--fee--color': '#82DCFA',
  'icon--co2--color': CAVA_COLORS.green800,
  'icon--default--color': '#FAB9FA'
};
const CavaTheme = {
  ...BaseTheme,
  brand: 'cava',
  brandName: 'AmTrav',
  logoInterstitial: 'BounceNextInterstitial',
  loader: {
    ...BaseLoader(globalApi$8)
  },
  global: globalApi$8,
  button: {
    ...BaseButton(globalApi$8),
    'primary--color': CAVA_COLORS.neutral950,
    'text--fontFamily': '"PerkSans", Arial, sans-serif'
  },
  card: {
    ...BaseCard(globalApi$8),
    'box-shadow': '0px 0px 2px 0px rgba(0, 0, 0, 0.12), 0px 1px 6px 0px rgba(0, 0, 0, 0.08), 0px 3px 12px 0px rgba(0, 0, 0, 0.08);'
  },
  chat: {
    ...BaseChat(globalApi$8),
    'minimized-chat--color': CAVA_COLORS.lime400,
    'minimized-icon--color': CAVA_COLORS.neutral950
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$8)
  },
  table: {
    ...BaseTable(globalApi$8)
  },
  textfield: {
    ...BaseTextfield(globalApi$8),
    'border--color': CAVA_COLORS.neutral600
  },
  breakpoints: BaseBreakpoints(globalApi$8),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    'hamburgerMobile--color': CAVA_COLORS.neutral950,
    'helpCenterBackground--color': CAVA_COLORS.neutral950,
    'navBorder--color': CAVA_COLORS.neutral500,
    navLogo: 'CavaNavigation'
  },
  stylesForConcept: generateIconLookup(globalApi$8)
};

const emburseSpendColors = {
  // INFO (modified with tinycolor)
  info1: '#0D74CE',
  // passport11
  info2: '#e7f1fa',
  info3: '#a3caec',
  info4: '#053157',
  // CORE (colors for multiple Emburse themes)
  checkpoint11_2: '#a12226',
  checkpoint11: '#CE2C31',
  checkpoint12: '#641723',
  checkpoint3: '#FEEBEC',
  checkpoint5: '#FFCDCE',
  departure11_2: '#1a6645',
  departure11: '#218358',
  departure12: '#193B2D',
  departure3: '#E6F6EB',
  departure5: '#C4E8D1',
  passport11_2: '#0A5AA1',
  passport11: '#0D74CE',
  passport12: '#113264',
  passport3: '#E6F4FE',
  passport5: '#C2E5FF',
  runway0: '#FFFFFF',
  runway1: '#FCFCFD',
  runway11: '#60646C',
  runway12: '#1C2024',
  runway2: '#F9F9FB',
  runway4: '#E8E8EC',
  runway6: '#D9D9E0',
  runway8: '#B9BBC6',
  winsock10: '#EF5F00',
  winsock12: '#582D1D',
  winsock3: '#FFEFD6',
  winsock5: '#FFD19A'
};
const globalApi$7 = {
  ...BaseGlobal,
  'primary--color': emburseSpendColors.passport11,
  'secondary--color': emburseSpendColors.passport12,
  'link--color': emburseSpendColors.passport11,
  'dark--color': emburseSpendColors.passport12,
  'light--color': emburseSpendColors.passport3,
  'danger--color': emburseSpendColors.checkpoint11,
  'warning--color': emburseSpendColors.winsock10,
  'success--color': emburseSpendColors.departure11,
  'info--color': emburseSpendColors.passport11,
  'disabled--color': emburseSpendColors.runway8,
  'border--color': emburseSpendColors.runway4,
  'border--focus--color': emburseSpendColors.runway6,
  'textBody--color': emburseSpendColors.runway11,
  'headings--color': emburseSpendColors.runway12,
  'pageHeaderText--color': emburseSpendColors.runway12,
  'pageHeaderBackground--color': 'white',
  'icon--contact--bgcolor': emburseSpendColors.runway2
  // TODO: add overrides for these
  // 'tertiary--color':
  // 'accent--color':
  // 'link--hover--color':
  // 'linkSecondary--color':
};
const EmburseTheme = {
  ...BaseTheme,
  brand: 'emburse',
  logoInterstitial: 'EmburseInterstitial',
  loader: {
    ...BaseLoader(globalApi$7)
  },
  global: globalApi$7,
  button: {
    ...BaseButton(globalApi$7),
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$7),
    'box-shadow': '0px 0px 2px 0px rgba(0, 0, 0, 0.12), 0px 1px 6px 0px rgba(0, 0, 0, 0.08), 0px 3px 12px 0px rgba(0, 0, 0, 0.08);'
  },
  chat: {
    ...BaseChat(globalApi$7),
    'minimized-chat--color': emburseSpendColors.passport11
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$7)
  },
  table: {
    ...BaseTable(globalApi$7),
    'primaryBackground--color': '#eee'
  },
  textfield: {
    ...BaseTextfield(globalApi$7),
    'background--color': '#fff'
  },
  breakpoints: BaseBreakpoints(globalApi$7),
  images: {
    ...BaseImages
  }
};

// aka Emburse Professional

const CertifyTheme = {
  ...EmburseTheme,
  brand: 'certify'
};

// aka Emburse Enterprise

const ChromeRiverTheme = {
  ...EmburseTheme,
  brand: 'chromeriver'
};

const eaColors = {
  blue1: '#0E51A8',
  blue2: '#0c4388',
  lightBlue: '#95c4ff',
  // nav
  info: '#156da8',
  red: '#D64541',
  orange: '#e87e04',
  green1: '#5fa444',
  green2: '#8dc577' // nav
};
const globalApi$6 = {
  ...BaseGlobal,
  'primary--color': eaColors.blue1,
  'link--color': eaColors.blue1,
  'danger--color': eaColors.red,
  'warning--color': eaColors.orange,
  'success--color': eaColors.green1,
  'info--color': eaColors.info,
  'pageHeaderText--color': eaColors.blue2,
  'headings--color': eaColors.blue2,
  'borderRadius--size': '0'
};
const EATheme = {
  ...BaseTheme,
  brand: 'ea',
  brandName: 'AmTrav',
  logoInterstitial: 'EAInterstitial',
  loader: {
    ...BaseLoader(globalApi$6)
  },
  global: globalApi$6,
  button: {
    ...BaseButton(globalApi$6),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$6),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$6),
    'minimized-chat--color': eaColors.blue1
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$6),
    'primary--color': eaColors.info
  },
  table: {
    ...BaseTable(globalApi$6)
  },
  textfield: {
    ...BaseTextfield(globalApi$6),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$6),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'EANavigation',
    showPoweredBy: true,
    'activeLink--color': eaColors.green2,
    'sectionTitle--color': eaColors.lightBlue
  }
};

const edenredPayColors = {
  white: '#FFFFFF',
  black: '#000000',
  // nav
  red: '#F72717',
  // nav
  vibrantRed: '#FF007D',
  cobalt: '#0D8AFF',
  // nav
  lightCobalt: '#B0EFFF',
  // nav
  accentCobalt: '#dff4f9',
  coldWhite: '#F1F7FF',
  infoCobalt: '#00529f',
  darkBlue: '#22222d',
  // nav
  violet: '#711CFF',
  olive: '#81C105',
  mint: '#00A184',
  orange: '#f79517',
  grey: '#6D7787',
  lightGrey: '#e9edf3'
};
const globalApi$5 = {
  ...BaseGlobal,
  'primary--color': edenredPayColors.cobalt,
  'secondary--color': edenredPayColors.darkBlue,
  'link--color': edenredPayColors.cobalt,
  'dark--color': edenredPayColors.darkBlue,
  'light--color': edenredPayColors.coldWhite,
  'accent--color': edenredPayColors.accentCobalt,
  'danger--color': edenredPayColors.vibrantRed,
  'warning--color': edenredPayColors.orange,
  'success--color': edenredPayColors.olive,
  'info--color': edenredPayColors.infoCobalt,
  'textBody--color': edenredPayColors.black,
  'pageHeaderText--color': edenredPayColors.black,
  'headings--color': edenredPayColors.black,
  'border--color': edenredPayColors.lightGrey,
  'borderRadius--size': '0'
};
const EdenredPayTheme = {
  ...BaseTheme,
  brand: 'edenredpay',
  brandName: 'AmTrav',
  logoInterstitial: 'EdenredPayInterstitial',
  loader: {
    ...BaseLoader(globalApi$5)
  },
  global: globalApi$5,
  button: {
    ...BaseButton(globalApi$5),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$5),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$5),
    'minimized-chat--color': edenredPayColors.cobalt
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$5)
  },
  table: {
    ...BaseTable(globalApi$5)
  },
  textfield: {
    ...BaseTextfield(globalApi$5),
    'background--color': 'white',
    'background--focus--color': edenredPayColors.coldWhite,
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$5),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'EdenredPayNavigation',
    'navBorder--color': 'rgba(159, 159, 159, 0.25)',
    'sectionTitle--color': edenredPayColors.red,
    'menuItemText--color': edenredPayColors.black,
    'contactBackground--color': edenredPayColors.darkBlue,
    'helpCenterBackground--color': edenredPayColors.lightCobalt,
    'helpCenterText--color': edenredPayColors.black,
    'hamburgerMobile--color': edenredPayColors.cobalt,
    showPoweredBy: false
  }
};

const lassoColors = {
  blue: '#008aab',
  info: '#156da8',
  red: '#D64541',
  orange: '#e87e04',
  green: '#5fa444',
  darkGrey: '#262626',
  // nav
  yellow: '#f5bf3b' // nav
};
const globalApi$4 = {
  ...BaseGlobal,
  'primary--color': lassoColors.blue,
  'link--color': lassoColors.blue,
  'link--hover--color': lassoColors.blue,
  'danger--color': lassoColors.red,
  'warning--color': lassoColors.orange,
  'success--color': lassoColors.green,
  'info--color': lassoColors.info,
  'pageHeaderText--color': '#000',
  'headings--color': '#000',
  'borderRadius--size': '0'
};
const LassoTheme = {
  ...BaseTheme,
  brand: 'lasso',
  brandName: 'AmTrav',
  logoInterstitial: 'LassoInterstitial',
  loader: {
    ...BaseLoader(globalApi$4)
  },
  global: globalApi$4,
  button: {
    ...BaseButton(globalApi$4),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$4),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$4)
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$4)
  },
  table: {
    ...BaseTable(globalApi$4)
  },
  textfield: {
    ...BaseTextfield(globalApi$4),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$4),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'LassoNavigation',
    'navBorder--color': 'rgba(255, 255, 255, 0.25)',
    'contactBackground--color': lassoColors.darkGrey,
    showPoweredBy: true,
    'activeLink--color': lassoColors.yellow
  }
};

const nexoniaColors = {
  // link
  azureBlue: '#2681DD',
  greenBlue: '#1C68B4',
  // hover
  yaleBlue: '#154E88',
  // active
  // navigation
  denimBlue: '#005EB8',
  // subhead
  carolinaBlue: '#0097DC',
  // secondary
  culturedWhite: '#F3F4F5',
  platinum: '#E5E7E9',
  // hover
  gainsboro: '#D7DBDE',
  // active
  // primary
  pantoneGreen: '#1FB64D',
  forestGreenWeb: '#188A3B',
  // hover
  cadmiumGreen: '#105F28',
  // active
  malachiteGreen: '#23D158',
  lightGreen: '#6fe593' // nav
};
const globalApi$3 = {
  ...BaseGlobal,
  'primary--color': nexoniaColors.pantoneGreen,
  'secondary--color': nexoniaColors.yaleBlue,
  'link--color': nexoniaColors.azureBlue,
  // primary not assigned to link
  'border--color': nexoniaColors.platinum,
  'border--focus--color': nexoniaColors.gainsboro,
  'info--color': nexoniaColors.greenBlue,
  'dark--color': nexoniaColors.yaleBlue,
  'light--color': nexoniaColors.culturedWhite,
  'success--color': nexoniaColors.malachiteGreen,
  'pageHeaderBackground--color': 'white',
  'borderRadius--size': '0'
};
const NexoniaTheme = {
  ...BaseTheme,
  brand: 'nexonia',
  brandName: 'AmTrav',
  logoInterstitial: 'NexoniaInterstitial',
  loader: {
    ...BaseLoader(globalApi$3)
  },
  global: globalApi$3,
  button: {
    ...BaseButton(globalApi$3),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$3),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$3),
    'minimized-chat--color': nexoniaColors.pantoneGreen
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$3),
    'primary--color': nexoniaColors.azureBlue
  },
  table: {
    ...BaseTable(globalApi$3),
    'primaryBackground--color': '#eee'
  },
  textfield: {
    ...BaseTextfield(globalApi$3),
    'background--color': '#fff',
    'prominentBackground--color': '#fff',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$3),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'NexoniaNavigation',
    showPoweredBy: true,
    'activeLink--color': nexoniaColors.lightGreen,
    'sectionTitle--color': nexoniaColors.lightGreen
  }
};

const SpendTheme = {
  ...EmburseTheme,
  brand: 'spend'
};

const sutisoftColors = {
  blue: '#0081c0',
  lightBlueJeans: '#41b0f7',
  // nav
  darkBlue: '#00496f',
  info: '#156da8',
  red: '#D64541',
  orange: '#e87e04',
  green: '#5fa444'
};
const globalApi$2 = {
  ...BaseGlobal,
  'primary--color': sutisoftColors.blue,
  'secondary--color': sutisoftColors.darkBlue,
  'link--color': sutisoftColors.blue,
  'dark--color': sutisoftColors.darkBlue,
  'danger--color': sutisoftColors.red,
  'warning--color': sutisoftColors.orange,
  'success--color': sutisoftColors.green,
  'info--color': sutisoftColors.info,
  'pageHeaderText--color': sutisoftColors.darkBlue,
  'headings--color': sutisoftColors.darkBlue,
  'borderRadius--size': '0'
};
const SutisoftTheme = {
  ...BaseTheme,
  brand: 'sutisoft',
  brandName: 'AmTrav',
  logoInterstitial: 'SutiSoftInterstitial',
  loader: {
    ...BaseLoader(globalApi$2)
  },
  global: globalApi$2,
  button: {
    ...BaseButton(globalApi$2),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$2),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$2),
    'minimized-chat--color': sutisoftColors.blue
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$2)
  },
  table: {
    ...BaseTable(globalApi$2)
  },
  textfield: {
    ...BaseTextfield(globalApi$2),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$2),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'SutiSoftNavigation',
    'navBorder--color': 'rgba(159, 159, 159, 0.25)',
    showPoweredBy: true,
    'activeLink--color': sutisoftColors.lightBlueJeans
  }
};

const svbColors = {
  darkBlue: '#007DB8',
  deepBlue: '#004B6C',
  cyanProcess: '#3ebded',
  prussianBlue: '#033149',
  lightBlue: '#33BBEF',
  // chat
  lightBlueMist: '#f7fdff',
  // table
  danger: '#B00020',
  warning: '#E77503',
  success: '#4BAF46',
  bkgdGrey: '#F0F0F0',
  lightGrey: '#DCDCDC'
};
const globalApi$1 = {
  ...BaseGlobal,
  'primary--color': svbColors.cyanProcess,
  'secondary--color': svbColors.prussianBlue,
  'tertiary--color': svbColors.lightBlue,
  'danger--color': svbColors.danger,
  'warning--color': svbColors.warning,
  'success--color': svbColors.success,
  'info--color': svbColors.deepBlue,
  'light--color': svbColors.lightBlueMist,
  'dark--color': svbColors.prussianBlue,
  'border--color': svbColors.lightGrey,
  'border--focus--color': svbColors.lightGrey,
  'link--color': svbColors.cyanProcess,
  'link--hover--color': svbColors.cyanProcess,
  'borderRadius--size': '0'
};
const SVBTheme = {
  ...BaseTheme,
  brand: 'svb',
  brandName: 'AmTrav',
  logoInterstitial: 'SVBInterstitial',
  loader: {
    ...BaseLoader(globalApi$1)
  },
  global: globalApi$1,
  button: {
    ...BaseButton(globalApi$1),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi$1),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi$1),
    'minimized-chat--color': svbColors.darkBlue
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi$1)
  },
  table: {
    ...BaseTable(globalApi$1),
    'primaryBackground--color': svbColors.lightBlueMist
  },
  textfield: {
    ...BaseTextfield(globalApi$1),
    'background--color': '#fff',
    'prominentBackground--color': '#fff',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi$1),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'SVBNavigation',
    'navBorder--color': 'rgba(159, 159, 159, 0.25)',
    showPoweredBy: true,
    'activeLink--color': svbColors.lightBlue
  }
};

const tallieColors = {
  lightGrey: '#f5f5f5',
  lightColor: '#f5f9f1',
  // nav
  green: '#6fa60f',
  lightGreen: '#7cbe00',
  lightGreenTitle: '#ceff71',
  // nav
  darkGreen: '#406009',
  info: '#424241'
};
const globalApi = {
  ...BaseGlobal,
  'primary--color': tallieColors.green,
  'secondary--color': tallieColors.darkGreen,
  'link--color': tallieColors.green,
  'link--hover--color': tallieColors.green,
  'light--color': tallieColors.lightColor,
  'info--color': tallieColors.info,
  'dark--color': tallieColors.darkGreen,
  'borderRadius--size': '0'
};
const TallieTheme = {
  ...BaseTheme,
  brand: 'tallie',
  brandName: 'AmTrav',
  logoInterstitial: 'TallieInterstitial',
  loader: {
    ...BaseLoader(globalApi)
  },
  global: globalApi,
  button: {
    ...BaseButton(globalApi),
    'borderRadius--size': '30px',
    'borderWidth--standard': '2px',
    borderTransparent: true
  },
  card: {
    ...BaseCard(globalApi),
    'borderFlush--width': '1px 0 1px 0',
    'borderMedium--width': '0',
    'box-shadow': bounceCardShadow
  },
  chat: {
    ...BaseChat(globalApi),
    'minimized-chat--color': tallieColors.green
  },
  svgPalette: {
    ...BaseSvgPalette(globalApi)
  },
  table: {
    ...BaseTable(globalApi)
  },
  textfield: {
    ...BaseTextfield(globalApi),
    'background--color': 'white',
    'borderWidth--standard': '2px',
    'box-shadow': bounceInputShadow
  },
  breakpoints: BaseBreakpoints(globalApi),
  images: {
    ...BaseImages
  },
  siteNavigation: {
    ...BaseSiteNavigation(),
    navLogo: 'TallieNavigation',
    showPoweredBy: true,
    'activeLink--color': tallieColors.lightColor,
    'sectionTitle--color': tallieColors.lightGreenTitle
  }
};

const themeNameLookup = {
  default: BaseTheme,
  a2b: A2bTheme,
  abacus: AbacusTheme,
  advisorconsole: AdvisorConsoleTheme,
  atom: ATOMTheme,
  bouncenext: BounceNextTheme,
  cava: CavaTheme,
  chromeriver: ChromeRiverTheme,
  certify: CertifyTheme,
  ea: EATheme,
  edenredpay: EdenredPayTheme,
  emburse: EmburseTheme,
  lasso: LassoTheme,
  nexonia: NexoniaTheme,
  spend: SpendTheme,
  sutisoft: SutisoftTheme,
  svb: SVBTheme,
  tallie: TallieTheme
};

/* eslint react/prop-types: 0 */
const DynamicThemeProvider = props => {
  const {
    themeName = 'bouncenext',
    overrides
  } = props;
  const brandTheme = themeNameLookup[themeName] || themeNameLookup.default;
  const brandWithOverrides = mergeDeepRight(brandTheme, overrides || {});
  return /*#__PURE__*/React.createElement(ThemeProvider, _extends({
    theme: brandWithOverrides
  }, props));
};

export { AbacusTheme as A, BounceNextTheme as B, CavaTheme as C, DynamicThemeProvider as D, EATheme as E, LassoTheme as L, NexoniaTheme as N, SpendTheme as S, TallieTheme as T, AdvisorConsoleTheme as a, ATOMTheme as b, A2bTheme as c, CertifyTheme as d, ChromeRiverTheme as e, EdenredPayTheme as f, EmburseTheme as g, SutisoftTheme as h, SVBTheme as i, themeNameLookup as t };
//# sourceMappingURL=withThemeProvider-1ef7b57b.js.map
